import { useCallback } from 'react';
import { Variant } from 'shared/types/product';
import { ProductQuery } from 'shared/types/query/ProductQuery';
import { sdk } from 'sdk';
import { Product } from 'types/entity/product';

// this hook is used from several PDP sections, so anything unconditional here
// runs on every one of them.
const useProduct = () => {
  const queryProducts = useCallback(async (productQuery: ProductQuery) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.query(productQuery);

    return res;
  }, []);

  const queryRecentlyViewedProduct = useCallback(async (productKey: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getRecentlyViewedProduct(productKey);

    return res;
  }, []);

  const queryProductSupplies = useCallback(async (supplies: string[]) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getProductSupplies(supplies);
    if (res.isError) throw new Error(res?.error?.message || 'Something went wrong');
    return res.data;
  }, []);

  const queryBulbRecommendations = useCallback(async (bulbBase: string, limit: number) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getBulbRecommendations(bulbBase, limit);
    if (res.isError) throw new Error(res?.error?.message || 'Something went wrong');
    return res.data;
  }, []);

  const queryRugPadRecommendations = useCallback(
    async (productKey: string, rugSize?: string, rugActualSize?: string) => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.product.getRugPadRecommendations(productKey, rugSize, rugActualSize);
      if (res.isError) throw new Error(res?.error?.message || 'Something went wrong');
      return res.data;
    },
    [],
  );

  const getAvailabilityMessage = useCallback(async (sku: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getAvailabilityMessage(sku);
    return res;
  }, []);

  const getShopCollection = useCallback(async (collection: string, productKey: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.product.getShopCollection({
      collection: collection,
      excludeSku: productKey,
    });
    return res;
  }, []);

  const getYouMayAlsoLike = useCallback(
    async (product: Product, excludeProductKeys: string[] = [], selectedVariant?: Variant) => {
      const extensions = sdk.composableCommerce;
      const res = await extensions.product.getYouMayAlsoLike({ product, excludeProductKeys, selectedVariant });
      return res;
    },
    [],
  );

  const shareProductLink = useCallback(
    async (
      email: string,
      pdpShareLink: string,
      productImageUrl?: string,
      senderName?: string,
      productName?: string,
      sku?: string,
    ) => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.product.shareProductLink({
        email,
        pdpShareLink,
        productImageUrl,
        senderName,
        productName,
        sku,
      });
      return res;
    },
    [],
  );

  return {
    query: queryProducts,
    queryRecentlyViewedProduct,
    getAvailabilityMessage,
    getShopCollection,
    getYouMayAlsoLike,
    queryProductSupplies,
    queryBulbRecommendations,
    queryRugPadRecommendations,
    shareProductLink,
  };
};

export default useProduct;
